/**
 * 倒计时控制器（单例模式）
 * 集中管理所有倒计时逻辑，并提供统一的服务器时间
 *
 * 功能特点：
 * 1. 确保所有组件使用统一的时间源
 * 2. 自动校准服务器时间与本地时间差异
 * 3. 精确到毫秒的时间计算
 * 4. 自动清理无订阅者的计时器
 */
class CountdownController {
  private static instance: CountdownController;
  private interval: NodeJS.Timeout | null | number = null;
  private callbacks: Map<string, (currentTime: number) => void> = new Map();
  private serverTimeOffset: number = 0; // 服务器时间与本地时间的差值
  private lastSyncTime: number = 0; // 最后一次同步时间（本地时间戳）
  private isTimeSynced: boolean = false; // 是否已同步服务器时间

  constructor() {
    if (CountdownController.instance) {
      return CountdownController.instance;
    }
    CountdownController.instance = this;
  }

  /**
   * 启动全局计时器
   */
  private start(): void {
    if (this.interval) return;

    // 使用requestAnimationFrame实现更精确的计时
    let lastTick = performance.now();
    const tick = () => {
      const now = performance.now();
      if (now - lastTick >= 1000) {
        lastTick = now;
        this.notify();
      }
      this.interval = requestAnimationFrame(tick);
    };
    this.interval = requestAnimationFrame(tick);
  }

  /**
   * 停止全局计时器
   */
  private stop(): void {
    if (this.interval) {
      cancelAnimationFrame(this.interval as number);
      this.interval = null;
    }
  }

  /**
   * 设置服务器时间（所有组件将基于此时间）
   * @param serverTimestamp 服务器时间戳（毫秒）
   * @param force 是否强制更新（默认false）
   */
  setServerDate(serverTimestamp: number, force: boolean = false): void {
    // 只允许校准一次，除非强制重新校准
    if (this.isTimeSynced && !force) return;

    const localNow = Date.now();
    const newOffset = serverTimestamp - localNow;
    const absOffset = Math.abs(newOffset);

    // 只有当时间偏移大于10秒时才进行校准
    if (absOffset > 10000) {
      // 10秒 = 10000毫秒
      this.serverTimeOffset = newOffset;
      this.lastSyncTime = localNow;
      this.isTimeSynced = true;

      console.debug(`[Countdown] 时间已校准，服务器时间偏移: ${this.serverTimeOffset}ms`);
    } else {
      // 时间偏移小于等于10秒，不进行校准，保持原偏移值为0或原有值
      // console.debug(`[Countdown] 时间偏移过小(${Math.floor(absOffset / 1000)}秒)，无需校准`);
    }
  }

  /**
   * 获取当前校准后的服务器时间
   */
  getCurrentTime(): number {
    // 如果未同步服务器时间，使用本地时间
    if (!this.isTimeSynced) return Date.now();

    // 计算从最后一次同步到现在的本地时间流逝
    const localElapsed = Date.now() - this.lastSyncTime;

    // 返回校准后的服务器时间（考虑本地时钟漂移）
    return this.lastSyncTime + this.serverTimeOffset + localElapsed;
  }

  /**
   * 订阅时间更新
   * @param id 唯一订阅ID
   * @param callback 回调函数，接收当前服务器时间
   */
  subscribe(id: string, callback: (currentTime: number) => void): void {
    this.callbacks.set(id, callback);
    this.start();
    // 立即触发一次更新，确保新订阅者获取最新时间
    callback(this.getCurrentTime());
  }

  /**
   * 取消订阅
   * @param id 要移除的订阅ID
   */
  unsubscribe(id: string): void {
    this.callbacks.delete(id);

    // 没有订阅者时停止计时器
    if (this.callbacks.size === 0) {
      this.stop();
    }
  }

  /**
   * 通知所有订阅者
   */
  private notify(): void {
    const currentTime = this.getCurrentTime();
    this.callbacks.forEach((callback) => {
      callback(currentTime);
    });
  }

  /**
   * 生成唯一订阅ID
   */
  generateEventId(): string {
    return `countdown-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default CountdownController;
